﻿/**
 * A utility class for working with URLs.
 */
export default class UrlExtensions {

  /**
   * Gets the subdirectory of a URL.
   * @param {URL} url - The URL to get the subdirectory from.
   * @returns {string|null} The subdirectory of the URL, or null if there is none.
   */
  static getSubdirectory(url) {
    const firstSegment = url.pathname?.split('/')?.[1];
    const subdirectory = firstSegment || null;

    return subdirectory;
  }

  /**
   * Removes the query string from a URL.
   * @param {string} url - The URL to remove the query string from.
   * @returns {string} The URL without the query string.
   */
  static removeQueryString(url) {
    if(!url){
      return url;
    }
    
    const urlWithoutQueryString = url?.split('?')[0];
    return urlWithoutQueryString;
  }

  /**
   * Gets the value of a query parameter from a URL.
   * @param {URL} url - The URL to get the parameter value from.
   * @param {string} parameter - The name of the parameter to get the value of.
   * @returns {string|null} The value of the parameter, or null if it doesn't exist.
   */
  static getParameterValue(url, parameter) {
    const searchParams = new URLSearchParams(url.search);
    return searchParams.get(parameter) ?? null;
  }

  /**
   * Gets the value of a query parameter from the URL hash.
   * @param {URL} url - The URL to get the parameter value from.
   * @param {string} parameter - The name of the parameter to get the value of.
   * @returns {string|null} The value of the parameter, or null if it doesn't exist.
   */
  static getParameterValueFromHash(url, parameter) {
    const search = url.hash.replace('#', '?');
    const searchParams = new URLSearchParams(search);
    return searchParams.get(parameter) ?? null;
  }

  /**
   * Sets query parameters on a URL.
   * @param {string} url - The URL to set the parameters on.
   * @param {Object.<string, string>} parameters - An object containing the parameter names and values to set.
   * @returns {string} The URL with the new parameters set.
   */
  static setParameters(url, parameters) {
    const separator = url.includes('?') ? '&' : '?';
    const query = [];
    Object.keys(parameters).forEach((key) => {
      const value = parameters[key];
      if (value !== null) {
        query.push(`${key}=${encodeURIComponent(value)}`);
      }
    });

    if (query.length) {
      return `${url}${separator}${query.join('&')}`;
    }

    return `${url}`;
  }
}
